import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '../../contexts/SettingsContext';
import logoImage from '../../assets/logo.png';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { businessInfo } = useSettings();
  const location = useLocation();
  const phone = businessInfo?.phone || '+263-71-708-2807';
  const email = businessInfo?.email || 'info@gandimall.com';
  const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, '')}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shops', label: 'Shops' },
    { to: '/client/login', label: 'Portal' }
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-200 ${
        scrolled ? 'bg-ink shadow-card' : 'bg-ink'
      }`}
    >
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-ink rounded-lg"
          >
            <img
              src={logoImage}
              alt={businessInfo?.name || 'The Gandi Mall'}
              className="h-9 sm:h-10 w-auto"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main">
            {navLinks.map(({ to, label }) => {
              const isActive =
                location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
              return (
                <Link
                  key={to}
                  to={to}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-white/10 text-white'
                      : 'text-stone-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {label}
                </Link>
              );
            })}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <Link
              to="/shops"
              className="ml-2 inline-flex items-center px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
            >
              View Listings
            </Link>
          </nav>

          <button
            type="button"
            className="md:hidden p-2 rounded-xl text-stone-300 hover:text-white hover:bg-white/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-ink"
          >
            <nav className="px-4 py-4 space-y-1" aria-label="Mobile">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="block py-3 px-3 rounded-xl text-stone-200 hover:bg-white/10 hover:text-white"
                >
                  {label}
                </Link>
              ))}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 py-3 px-3 rounded-xl text-[#25D366] hover:bg-white/5"
              >
                <MessageCircle size={20} /> WhatsApp
              </a>
              <a
                href={`tel:${phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 py-3 px-3 rounded-xl text-stone-200 hover:bg-white/10"
              >
                <Phone size={20} /> Call
              </a>
              <Link
                to="/shops"
                className="block py-3 px-3 rounded-xl bg-primary text-white font-semibold text-center mt-2"
              >
                View Listings
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
